import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { categories } from "@/mocks/categories";

const FORM_URL = "https://readdy.ai/api/form/da227ldv7gurs51ef480";

type Status = "idle" | "loading" | "success" | "error";

const fieldCls =
  "w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 placeholder:text-foreground-300 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("website_alt") as string | null)?.trim() ?? "";

    // 机器人陷阱：命中时不提交，仅给出与正常提交一致的成功反馈
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      body.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam|form data is spam/i.test(serverMsg || "");

      if (!res.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <>
      <Seo
        title="留言讨论 · 师兄啊师兄剧情讨论区"
        description="与师兄啊师兄同好交流剧情解读、投稿剧情分析或反馈建议。欢迎留下你的观点，一起把宗门篇、修炼境界与主线伏笔聊透。"
        keywords="师兄啊师兄留言,师兄啊师兄剧情讨论,师兄啊师兄投稿,师兄啊师兄同好交流"
        type="website"
        path="/contact"
      />

      <section className="border-b border-background-200 bg-background-100/60 py-12 md:py-16">
        <div className="container-x">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-600">留言讨论</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">一起聊聊剧情</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
            分享你的剧情解读、伏笔发现或角色感悟，也可以投稿一篇分析文章。我们会认真阅读每一条留言。
          </p>
        </div>
      </section>

      <section className="container-x grid grid-cols-1 gap-10 py-12 md:py-16 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">留言须知</h2>
          <ul className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-600">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-chat-smile-line text-sm"></i>
              </span>
              友善交流，尊重不同观点；涉及剧透请先注明章节范围。
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-file-edit-line text-sm"></i>
              </span>
              投稿剧情分析时，请尽量给出具体章节与你的推理依据。
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-mail-line text-sm"></i>
              </span>
              如需单独联系，也可发送邮件至 hello@example.com。
            </li>
          </ul>

          <div className="mt-8 rounded-xl border border-background-200/70 bg-background-100/60 p-5">
            <h3 className="font-heading text-sm font-semibold text-foreground-950">常见入口</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/section/${c.slug}`}
                  className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <form
            onSubmit={handleSubmit}
            data-readdy-form="师兄啊师兄讨论区留言表单"
            className="rounded-xl border border-background-200/70 bg-background-50 p-6 shadow-soft md:p-8"
            noValidate
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  你的称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={30}
                  placeholder="怎么称呼你"
                  className={fieldCls}
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  邮箱地址
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className={fieldCls}
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-800">
                想聊的栏目
              </label>
              <select id="category" name="category" className={fieldCls}>
                {categories.map((c) => (
                  <option key={c.slug} value={c.name}>
                    {c.name}
                  </option>
                ))}
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="写下你想聊的剧情解读、伏笔发现或投稿内容…"
                className={`${fieldCls} resize-none`}
              ></textarea>
              <p className="mt-1.5 text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            <input
              type="text"
              name="website_alt"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-foreground-400">提交即代表你同意以友善方式参与讨论。</p>
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-base"></i>
                    提交留言
                  </>
                )}
              </button>
            </div>

            {status === "success" && (
              <div className="mt-5 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4 text-sm text-accent-800">
                <i className="ri-checkbox-circle-line mt-0.5 text-lg"></i>
                <p>留言已收到，感谢分享！我们会认真阅读，有需要时通过邮箱与你联系。</p>
              </div>
            )}
            {status === "error" && (
              <div className="mt-5 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800">
                <i className="ri-error-warning-line mt-0.5 text-lg"></i>
                <p>{formError}</p>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}